import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Heading,
  VStack,
  Text,
  Badge,
  HStack,
  Button,
  useToast,
  Card,
  CardHeader,
  CardBody,
  Icon,
  Flex,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  SimpleGrid,
  Skeleton,
} from "@chakra-ui/react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { gql, useQuery } from "@apollo/client";
import {
  ExternalLinkIcon,
  CalendarIcon,
  TimeIcon,
  InfoIcon,
} from "@chakra-ui/icons";
import {
  FaVideo,
  FaMoneyBillWave,
  FaCheckCircle,
  FaExclamationCircle,
} from "react-icons/fa";

import { NavbarWithCallToAction } from "../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { useAuth } from "../../contexts/AuthContext";
import { getColor, getComponent, brandConfig } from "../../brandConfig";

// GraphQL query to get booking details by ID
const GET_BOOKING_BY_ID = gql`
  query GetBookingById($bookingId: String!) {
    bookingById(bookingId: $bookingId) {
      id
      bookingTime
      startTime
      endTime
      status
      sendReminders
      cancellationReason
      payment {
        status
        method
        amount
        currency
        paidAt
      }
      sessionType {
        id
        name
        description
        format
        duration
        price
        color
        meetingLink
      }
      client {
        id
        fName
        lName
        email
        phoneNumber
      }
      practitionerId
    }
  }
`;

// Add query to get practitioner details
const GET_PRACTITIONER_BY_ID = gql`
  query PractitionerById($id: ID!) {
    practionerById(id: $id) {
      id
      fName
      lName
      email
      phoneNumber
      businessName
    }
  }
`;

// Helper function to format date
const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString(undefined, {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

// Helper function to format time
const formatTime = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });
};

// Helper to get status color
const getStatusColor = (status: string): string => {
  switch (status) {
    case "CONFIRMED":
      return "blue";
    case "COMPLETED":
      return "green";
    case "CANCELLED":
      return "red";
    case "PENDING":
      return "yellow";
    default:
      return "gray";
  }
};

// Helper to get payment status color
const getPaymentStatusColor = (status: string): string => {
  switch (status) {
    case "COMPLETED":
      return "green";
    case "PENDING":
      return "yellow";
    case "PROCESSING":
      return "blue";
    case "FAILED":
      return "red";
    case "REFUNDED":
      return "purple";
    default:
      return "gray";
  }
};

// Component to display countdown
const CountdownTimer = ({ targetDate, onComplete }: { targetDate: Date; onComplete: () => void }) => {
  const calculateTimeLeft = () => {
    const difference = targetDate.getTime() - new Date().getTime();

    if (difference <= 0) {
      return {
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
        isCompleted: true,
      };
    }

    return {
      days: Math.floor(difference / (1000 * 60 * 60 * 24)),
      hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((difference / 1000 / 60) % 60),
      seconds: Math.floor((difference / 1000) % 60),
      isCompleted: false,
    };
  };

  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());

  useEffect(() => {
    const timer = setInterval(() => {
      const newTimeLeft = calculateTimeLeft();
      setTimeLeft(newTimeLeft);

      // Call onComplete when timer finishes
      if (newTimeLeft.isCompleted && !timeLeft.isCompleted) {
        onComplete();
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [targetDate, timeLeft.isCompleted, onComplete]);

  if (timeLeft.isCompleted) {
    return (
      <Box
        p={4}
        bg="rgba(34, 197, 94, 0.1)"
        borderRadius="md"
        borderLeft="4px solid"
        border="1px solid"
        borderColor="rgba(34, 197, 94, 0.3)"
        boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.37)"
      >
        <HStack>
          <Icon as={FaCheckCircle} color={getColor("successGreen")} />
          <Text fontWeight="bold" color={getColor("successGreen")} fontFamily={brandConfig.fonts.heading}>
            The session is now live!
          </Text>
        </HStack>
      </Box>
    );
  }

  return (
    <Box
      p={4}
      bg={getColor("background.cardGradient")}
      backdropFilter="blur(10px)"
      borderRadius="md"
      borderLeft="4px solid"
      border="1px solid"
      borderColor={getColor("border.darkCard")}
      boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.37)"
    >
      <Text fontWeight="bold" mb={3} color={getColor("primary")} fontFamily={brandConfig.fonts.heading}>
        Session starts in:
      </Text>
      <SimpleGrid columns={4} spacing={2} textAlign="center">
        <Box>
          <Box fontSize="2xl" fontWeight="bold" color={getColor("primary")} fontFamily={brandConfig.fonts.heading}>
            {timeLeft.days}
          </Box>
          <Text fontSize="xs" color={getColor("text.mutedDark")} fontFamily={brandConfig.fonts.body}>
            days
          </Text>
        </Box>
        <Box>
          <Box fontSize="2xl" fontWeight="bold" color={getColor("primary")} fontFamily={brandConfig.fonts.heading}>
            {timeLeft.hours}
          </Box>
          <Text fontSize="xs" color={getColor("text.mutedDark")} fontFamily={brandConfig.fonts.body}>
            hours
          </Text>
        </Box>
        <Box>
          <Box fontSize="2xl" fontWeight="bold" color={getColor("primary")} fontFamily={brandConfig.fonts.heading}>
            {timeLeft.minutes}
          </Box>
          <Text fontSize="xs" color={getColor("text.mutedDark")} fontFamily={brandConfig.fonts.body}>
            minutes
          </Text>
        </Box>
        <Box>
          <Box fontSize="2xl" fontWeight="bold" color={getColor("primary")} fontFamily={brandConfig.fonts.heading}>
            {timeLeft.seconds}
          </Box>
          <Text fontSize="xs" color={getColor("text.mutedDark")} fontFamily={brandConfig.fonts.body}>
            seconds
          </Text>
        </Box>
      </SimpleGrid>
    </Box>
  );
};

export const BookingDetails = () => {
  const { bookingId } = useParams<{ bookingId: string }>();
  const toast = useToast();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [isLive, setIsLive] = useState(false);

  // Brand styling variables
  const bgMain = getColor("background.main");
  const cardGradientBg = getColor("background.cardGradient");
  const cardBorder = getColor("border.darkCard");
  const textPrimary = getColor("text.primaryDark");
  const textSecondary = getColor("text.secondaryDark");
  const textMuted = getColor("text.mutedDark");
  const primaryColor = getColor("primary");
  const primaryHover = getColor("primaryHover");
  const successGreen = getColor("successGreen");
  const errorRed = getColor("status.error");
  const infoBlue = getColor("status.info");
  const warningColor = getColor("status.warning");

  const { data, loading, error } = useQuery(GET_BOOKING_BY_ID, {
    variables: { bookingId },
    skip: !bookingId,
    fetchPolicy: "network-only",
    onError: (error) => {
      console.error("Error fetching booking details:", error);
      toast({
        title: "Error",
        description: "Failed to load booking details. " + error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    },
  });

  // Fetch practitioner details when practitionerId is available
  const { data: practitionerData, loading: practitionerLoading } = useQuery(GET_PRACTITIONER_BY_ID, {
    variables: { id: data?.bookingById?.practitionerId || "" },
    skip: !data?.bookingById?.practitionerId,
    onError: (error) => {
      console.error("Error fetching practitioner details:", error);
    }
  });

  const booking = data?.bookingById;
  const sessionType = booking?.sessionType;
  const practitioner = practitionerData?.practionerById;

  // Update this logic to also consider our local state
  const isSessionLive =
    isLive || (booking?.startTime && new Date(booking.startTime) <= new Date());

  // Add check if session is over (1.5 hours after start time)
  const isSessionOver = booking?.startTime &&
    (new Date().getTime() - new Date(booking.startTime).getTime() > 1.5 * 60 * 60 * 1000);

  // Handler for timer completion
  const handleTimerComplete = () => {
    setIsLive(true);
  };

  if (loading) {
    return (
      <Box bg={bgMain} minH="100vh" display="flex" flexDirection="column">
        <NavbarWithCallToAction />
        <Container maxW="container.lg" py={8} flex="1">
          <VStack spacing={6} align="stretch">
            <Skeleton height="40px" width="70%" />
            <Skeleton height="200px" />
            <Skeleton height="120px" />
            <Skeleton height="150px" />
          </VStack>
        </Container>
        <Box position="sticky" bottom={0} zIndex={10}>
          <FooterWithFourColumns />
        </Box>
      </Box>
    );
  }

  if (error || !booking) {
    return (
      <Box bg={bgMain} minH="100vh" display="flex" flexDirection="column">
        <NavbarWithCallToAction />
        <Container maxW="container.lg" py={8} flex="1">
          <Box
            p={5}
            bg="rgba(239, 68, 68, 0.1)"
            borderRadius="md"
            borderLeft="4px solid"
            border="1px solid"
            borderColor="rgba(239, 68, 68, 0.3)"
            boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.37)"
          >
            <Heading size="md" mb={3} fontFamily={brandConfig.fonts.heading} color={errorRed}>
              Error Loading Booking
            </Heading>
            <Text color={textSecondary} fontFamily={brandConfig.fonts.body}>{error ? error.message : "Booking not found"}</Text>
            <Button
              as={Link}
              to="/sessions"
              mt={4}
              bg={primaryColor}
              color="white"
              size="sm"
              _hover={{
                bg: primaryHover,
                transform: "translateY(-2px)"
              }}
              _active={{ transform: "translateY(0)" }}
              transition="all 0.2s"
              fontWeight="semibold"
              fontFamily={brandConfig.fonts.body}
            >
              Return to Sessions
            </Button>
          </Box>
        </Container>
        <Box position="sticky" bottom={0} zIndex={10}>
          <FooterWithFourColumns />
        </Box>
      </Box>
    );
  }

  return (
    <Box bg={bgMain} minH="100vh" display="flex" flexDirection="column">
      <NavbarWithCallToAction />
      <Container maxW="container.lg" py={8} flex="1">
        <VStack spacing={6} align="stretch">
          <Box>
            <Heading size="lg" mb={2} fontFamily={brandConfig.fonts.heading} color={textPrimary}>
              Booking Details
            </Heading>
            <Text color={textMuted} fontFamily={brandConfig.fonts.body}>Reference: {booking.id}</Text>
          </Box>

          {/* Meeting Participants */}
          <Flex
            direction={{ base: "column", md: "row" }}
            gap={{ base: 4, md: 6 }}
            bg={cardGradientBg}
            backdropFilter="blur(10px)"
            p={5}
            borderRadius="lg"
            boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.37)"
            border="1px solid"
            borderColor={cardBorder}
            _hover={{
              transform: "translateY(-2px)",
              boxShadow: "0 12px 40px 0 rgba(0, 0, 0, 0.5)",
              transition: "all 0.2s"
            }}
          >
            {/* Practitioner Details */}
            <Box
              flex="1"
              p={4}
              bg="rgba(255, 255, 255, 0.05)"
              borderRadius="md"
              boxShadow="sm"
              border="1px solid"
              borderColor={cardBorder}
            >
              <Heading size="sm" mb={2} color={primaryColor} fontFamily={brandConfig.fonts.heading}>
                Practitioner
              </Heading>
              {practitionerLoading ? (
                <VStack align="start" spacing={2}>
                  <Skeleton height="20px" width="150px" />
                  <Skeleton height="20px" width="180px" />
                  <Skeleton height="20px" width="130px" />
                </VStack>
              ) : practitioner ? (
                <VStack align="start" spacing={1}>
                  <Text fontWeight="bold" fontFamily={brandConfig.fonts.heading} color={textPrimary}>
                    {practitioner.fName} {practitioner.lName}
                  </Text>
                  {practitioner.businessName && (
                    <Text fontSize="sm" color={textMuted} fontFamily={brandConfig.fonts.body}>{practitioner.businessName}</Text>
                  )}
                  {practitioner.email && (
                    <Text fontSize="sm" color={textSecondary} fontFamily={brandConfig.fonts.body}>{practitioner.email}</Text>
                  )}
                </VStack>
              ) : (
                <Text color={textMuted} fontFamily={brandConfig.fonts.body}>Practitioner information not available</Text>
              )}
            </Box>

            {/* Client Details Box */}
            <Box
              flex="1"
              p={4}
              bg="rgba(255, 255, 255, 0.05)"
              borderRadius="md"
              boxShadow="sm"
              border="1px solid"
              borderColor={cardBorder}
            >
              <Heading size="sm" mb={2} color={getColor("secondaryColor")} fontFamily={brandConfig.fonts.heading}>
                Client
              </Heading>
              {booking.client ? (
                <VStack align="start" spacing={1}>
                  <Text fontWeight="bold" fontFamily={brandConfig.fonts.heading} color={textPrimary}>
                    {booking.client.fName} {booking.client.lName}
                  </Text>
                  {booking.client.email && (
                    <Text fontSize="sm" color={textSecondary} fontFamily={brandConfig.fonts.body}>{booking.client.email}</Text>
                  )}
                </VStack>
              ) : (
                <Text color={textMuted} fontFamily={brandConfig.fonts.body}>Client information not available</Text>
              )}
            </Box>
          </Flex>

          {/* Countdown Timer */}
          {booking.status === "CONFIRMED" &&
            booking.startTime &&
            new Date(booking.startTime) > new Date() && (
              <CountdownTimer
                targetDate={new Date(booking.startTime)}
                onComplete={handleTimerComplete}
              />
            )}

          {/* Meeting Link Button (shows only when session is live and confirmed) */}
          {booking.status === "CONFIRMED" &&
            isSessionLive &&
            !isSessionOver &&
            sessionType?.meetingLink && (
              <Box
                p={4}
                bg="rgba(34, 197, 94, 0.1)"
                borderRadius="md"
                textAlign="center"
                border="1px solid"
                borderColor="rgba(34, 197, 94, 0.3)"
                boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.37)"
              >
                <Heading size="md" mb={3} fontFamily={brandConfig.fonts.heading} color={successGreen}>
                  Your Session is Live!
                </Heading>
                <Button
                  as="a"
                  href={sessionType.meetingLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  bg={primaryColor}
                  color="white"
                  size="lg"
                  leftIcon={<FaVideo />}
                  rightIcon={<ExternalLinkIcon />}
                  _hover={{
                    bg: primaryHover,
                    transform: "translateY(-2px)"
                  }}
                  _active={{ transform: "translateY(0)" }}
                  transition="all 0.2s"
                  fontWeight="semibold"
                  fontFamily={brandConfig.fonts.body}
                >
                  Join Meeting Now
                </Button>
              </Box>
            )}

          {/* Show Meeting Over message when session is over */}
          {booking.status === "CONFIRMED" &&
            isSessionOver && (
              <Box
                p={4}
                bg={cardGradientBg}
                backdropFilter="blur(10px)"
                borderRadius="md"
                textAlign="center"
                border="1px solid"
                borderColor={cardBorder}
                boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.37)"
              >
                <Heading size="md" mb={3} color={textMuted} fontFamily={brandConfig.fonts.heading}>
                  Meeting is Over
                </Heading>
                <Text color={textSecondary} fontFamily={brandConfig.fonts.body}>
                  This session has ended. Thank you for attending.
                </Text>
              </Box>
            )}

          {/* Booking Status */}
          <Card
            variant="outline"
            bg={cardGradientBg}
            backdropFilter="blur(10px)"
            borderRadius="lg"
            boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.37)"
            border="1px solid"
            borderColor={cardBorder}
            _hover={{
              transform: "translateY(-2px)",
              boxShadow: "0 12px 40px 0 rgba(0, 0, 0, 0.5)",
              transition: "all 0.2s"
            }}
          >
            <CardHeader bg="rgba(255, 255, 255, 0.05)" py={3}>
              <HStack>
                <Icon
                  as={
                    booking.status === "CONFIRMED"
                      ? FaCheckCircle
                      : booking.status === "CANCELLED"
                        ? FaExclamationCircle
                        : InfoIcon
                  }
                  color={booking.status === "CONFIRMED" ? primaryColor : booking.status === "CANCELLED" ? errorRed : textMuted}
                />
                <Heading size="md" fontFamily={brandConfig.fonts.heading} color={textPrimary}>
                  Status:{" "}
                  <Badge
                    bg={booking.status === "CONFIRMED" ? "rgba(34, 197, 94, 0.2)" : booking.status === "CANCELLED" ? "rgba(239, 68, 68, 0.2)" : "rgba(156, 163, 175, 0.2)"}
                    color={booking.status === "CONFIRMED" ? successGreen : booking.status === "CANCELLED" ? errorRed : textMuted}
                    border="1px solid"
                    borderColor={booking.status === "CONFIRMED" ? "rgba(34, 197, 94, 0.3)" : booking.status === "CANCELLED" ? "rgba(239, 68, 68, 0.3)" : "rgba(156, 163, 175, 0.3)"}
                    fontSize="md"
                    py={1}
                    px={2}
                  >
                    {booking.status}
                  </Badge>
                </Heading>
              </HStack>
            </CardHeader>
            <CardBody>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <Box>
                  <Stat>
                    <StatLabel color={textMuted} fontFamily={brandConfig.fonts.body}>Session Date</StatLabel>
                    <StatNumber fontSize="lg" fontFamily={brandConfig.fonts.heading} color={textPrimary}>
                      {booking.startTime
                        ? formatDate(booking.startTime)
                        : "N/A"}
                    </StatNumber>
                    <StatHelpText>
                      <HStack>
                        <CalendarIcon />
                        <Text fontFamily={brandConfig.fonts.body}>Booked on {formatDate(booking.bookingTime)}</Text>
                      </HStack>
                    </StatHelpText>
                  </Stat>
                </Box>

                <Box>
                  <Stat>
                    <StatLabel color={textMuted} fontFamily={brandConfig.fonts.body}>Session Time</StatLabel>
                    <StatNumber fontSize="lg" fontFamily={brandConfig.fonts.heading} color={textPrimary}>
                      {booking.startTime
                        ? formatTime(booking.startTime)
                        : "N/A"}{" "}
                      - {booking.endTime ? formatTime(booking.endTime) : "N/A"}
                    </StatNumber>
                    <StatHelpText>
                      <HStack>
                        <TimeIcon />
                        <Text fontFamily={brandConfig.fonts.body}>{sessionType?.duration} minutes</Text>
                      </HStack>
                    </StatHelpText>
                  </Stat>
                </Box>
              </SimpleGrid>

              {booking.cancellationReason && (
                <Box
                  mt={4}
                  p={3}
                  bg="rgba(239, 68, 68, 0.1)"
                  borderRadius="md"
                  border="1px solid"
                  borderColor="rgba(239, 68, 68, 0.3)"
                >
                  <Text fontWeight="bold" fontFamily={brandConfig.fonts.heading} color={errorRed}>
                    Cancellation Reason:
                  </Text>
                  <Text color={textSecondary} fontFamily={brandConfig.fonts.body}>{booking.cancellationReason}</Text>
                </Box>
              )}
            </CardBody>
          </Card>

          {/* Session Details */}
          <Card
            variant="outline"
            bg={cardGradientBg}
            backdropFilter="blur(10px)"
            borderRadius="lg"
            boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.37)"
            border="1px solid"
            borderColor={cardBorder}
            _hover={{
              transform: "translateY(-2px)",
              boxShadow: "0 12px 40px 0 rgba(0, 0, 0, 0.5)",
              transition: "all 0.2s"
            }}
          >
            <CardHeader bg="rgba(255, 255, 255, 0.05)" py={3}>
              <Heading size="md" fontFamily={brandConfig.fonts.heading} color={textPrimary}>
                Session Details
              </Heading>
            </CardHeader>
            <CardBody>
              <VStack align="stretch" spacing={4}>
                <Flex justify="space-between" flexWrap="wrap">
                  <Box flex="1" minW="200px">
                    <Heading size="sm" mb={1} fontFamily={brandConfig.fonts.heading} color={textMuted}>
                      Session Type
                    </Heading>
                    <Text fontSize="xl" fontWeight="bold" fontFamily={brandConfig.fonts.heading} color={textPrimary}>
                      {sessionType?.name || "N/A"}
                    </Text>
                  </Box>

                  <Flex gap={2} flexWrap="wrap">
                    <Badge bg="rgba(139, 92, 246, 0.2)" color={getColor("purpleAccent")} border="1px solid" borderColor="rgba(139, 92, 246, 0.3)" fontSize="md" px={2} py={1}>
                      {sessionType?.format || "N/A"}
                    </Badge>
                    <Badge bg="rgba(59, 130, 246, 0.2)" color={infoBlue} border="1px solid" borderColor="rgba(59, 130, 246, 0.3)" fontSize="md" px={2} py={1}>
                      {sessionType?.duration || "N/A"} min
                    </Badge>
                    <Badge bg="rgba(34, 197, 94, 0.2)" color={successGreen} border="1px solid" borderColor="rgba(34, 197, 94, 0.3)" fontSize="md" px={2} py={1}>
                      ${sessionType?.price || "N/A"}
                    </Badge>
                  </Flex>
                </Flex>

                {sessionType?.description && (
                  <Box>
                    <Heading size="sm" mb={1} fontFamily={brandConfig.fonts.heading} color={textMuted}>
                      Description
                    </Heading>
                    <Text color={textSecondary} fontFamily={brandConfig.fonts.body}>{sessionType.description}</Text>
                  </Box>
                )}

                {/* Display meeting link information if available but not yet live */}
                {sessionType?.meetingLink &&
                  !isSessionLive &&
                  booking.status === "CONFIRMED" && (
                    <Box
                      p={3}
                      bg="rgba(255, 255, 255, 0.05)"
                      borderRadius="md"
                      border="1px solid"
                      borderColor={cardBorder}
                    >
                      <Heading size="sm" mb={1} fontFamily={brandConfig.fonts.heading} color={primaryColor}>
                        Online Meeting
                      </Heading>
                      <Text mb={2} color={textSecondary} fontFamily={brandConfig.fonts.body}>
                        This session will take place online. The meeting link
                        will be available when the session starts.
                      </Text>
                    </Box>
                  )}
              </VStack>
            </CardBody>
          </Card>

          {/* Payment Details */}
          <Card
            variant="outline"
            bg={cardGradientBg}
            backdropFilter="blur(10px)"
            borderRadius="lg"
            boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.37)"
            border="1px solid"
            borderColor={cardBorder}
            _hover={{
              transform: "translateY(-2px)",
              boxShadow: "0 12px 40px 0 rgba(0, 0, 0, 0.5)",
              transition: "all 0.2s"
            }}
          >
            <CardHeader bg="rgba(255, 255, 255, 0.05)" py={3}>
              <HStack>
                <Icon as={FaMoneyBillWave} color={getColor("secondaryColor")} />
                <Heading size="md" fontFamily={brandConfig.fonts.heading} color={textPrimary}>
                  Payment Details
                </Heading>
              </HStack>
            </CardHeader>
            <CardBody>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <Box>
                  <Heading size="sm" mb={1} fontFamily={brandConfig.fonts.heading} color={textMuted}>
                    Payment Status
                  </Heading>
                  <Badge
                    bg={booking.payment?.status === "COMPLETED" ? "rgba(34, 197, 94, 0.2)" : booking.payment?.status === "PENDING" ? "rgba(255, 193, 7, 0.2)" : "rgba(156, 163, 175, 0.2)"}
                    color={booking.payment?.status === "COMPLETED" ? successGreen : booking.payment?.status === "PENDING" ? warningColor : textMuted}
                    border="1px solid"
                    borderColor={booking.payment?.status === "COMPLETED" ? "rgba(34, 197, 94, 0.3)" : booking.payment?.status === "PENDING" ? "rgba(255, 193, 7, 0.3)" : "rgba(156, 163, 175, 0.3)"}
                    fontSize="md"
                    px={2}
                    py={1}
                  >
                    {booking.payment?.status || "N/A"}
                  </Badge>
                </Box>

                <Box>
                  <Heading size="sm" mb={1} fontFamily={brandConfig.fonts.heading} color={textMuted}>
                    Amount
                  </Heading>
                  <Text fontSize="xl" fontWeight="bold" fontFamily={brandConfig.fonts.heading} color={textPrimary}>
                    ${booking.payment?.amount || "N/A"}{" "}
                    {booking.payment?.currency || "USD"}
                  </Text>
                </Box>

                <Box>
                  <Heading size="sm" mb={1} fontFamily={brandConfig.fonts.heading} color={textMuted}>
                    Payment Method
                  </Heading>
                  <Text color={textSecondary} fontFamily={brandConfig.fonts.body}>{booking.payment?.method || "N/A"}</Text>
                </Box>

                {booking.payment?.paidAt && (
                  <Box>
                    <Heading size="sm" mb={1} fontFamily={brandConfig.fonts.heading} color={textMuted}>
                      Payment Date
                    </Heading>
                    <Text color={textSecondary} fontFamily={brandConfig.fonts.body}>
                      {formatDate(booking.payment.paidAt)} at{" "}
                      {formatTime(booking.payment.paidAt)}
                    </Text>
                  </Box>
                )}
              </SimpleGrid>
            </CardBody>
          </Card>

          {/* Actions */}
          <HStack spacing={4} justify="center" mt={4}>
            <Button
              as={Link}
              to="/sessions"
              variant="outline"
              borderColor={cardBorder}
              color={textSecondary}
              _hover={{ bg: "rgba(255, 255, 255, 0.1)" }}
              fontFamily={brandConfig.fonts.body}
            >
              Back to Sessions
            </Button>

            {booking.status === "CONFIRMED" && (
              <Button
                as={Link}
                to={`/sessions/bookings/${booking.id}/cancel`}
                variant="outline"
                borderColor="rgba(239, 68, 68, 0.3)"
                color={errorRed}
                _hover={{ bg: "rgba(239, 68, 68, 0.1)" }}
                fontFamily={brandConfig.fonts.body}
              >
                Cancel Booking
              </Button>
            )}
          </HStack>
        </VStack>
      </Container>
      <Box position="sticky" bottom={0} zIndex={10}>
        <FooterWithFourColumns />
      </Box>
    </Box>
  );
};
